import getRouterBasename from '@/lib/router';
import { useSyncExternalStore } from 'react';

/** Path relative to the router basename (always starts with `/`). */
export function pathWithoutBasename(
  pathname: string = window.location.pathname
): string {
  const base = (getRouterBasename() || '').replace(/\/$/, '');
  if (!base) {
    return pathname || '/';
  }
  // Require exact match or a slash after the base so `/app` does not match `/application`.
  if (pathname === base) {
    return '/';
  }
  if (pathname.startsWith(`${base}/`)) {
    return pathname.slice(base.length) || '/';
  }
  return pathname;
}

/** True when the current (or given) path is a public share page. */
export function isSharePath(pathname?: string): boolean {
  return pathWithoutBasename(pathname ?? window.location.pathname).startsWith(
    '/share/'
  );
}

/** Prefix an app API path with the router basename when deployed under a subpath. */
export function appApiPath(path: string): string {
  const base = getRouterBasename().replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

/** Build the public share page URL from the current browser origin + basename. */
export function buildSharePageUrl(token: string): string {
  const base = getRouterBasename().replace(/\/$/, '');
  return `${window.location.origin}${base}/share/${encodeURIComponent(token)}`;
}

// Shared subscription so App (outside RouterProvider) still sees SPA navigations.
const pathnameListeners = new Set<() => void>();
let historyPatched = false;
let originalPushState: History['pushState'];
let originalReplaceState: History['replaceState'];

function notifyPathnameListeners() {
  pathnameListeners.forEach((listener) => listener());
}

function ensureHistoryPatched() {
  if (historyPatched || typeof window === 'undefined') return;
  historyPatched = true;
  originalPushState = window.history.pushState.bind(window.history);
  originalReplaceState = window.history.replaceState.bind(window.history);
  window.history.pushState = (...args: Parameters<History['pushState']>) => {
    const result = originalPushState(...args);
    notifyPathnameListeners();
    return result;
  };
  window.history.replaceState = (
    ...args: Parameters<History['replaceState']>
  ) => {
    const result = originalReplaceState(...args);
    notifyPathnameListeners();
    return result;
  };
  window.addEventListener('popstate', notifyPathnameListeners);
}

function subscribeToPathname(onStoreChange: () => void) {
  ensureHistoryPatched();
  pathnameListeners.add(onStoreChange);
  return () => {
    pathnameListeners.delete(onStoreChange);
  };
}

/**
 * Current browser pathname that updates on SPA navigations.
 * Needed because App wraps RouterProvider and cannot use useLocation.
 */
export function useBrowserPathname(): string {
  return useSyncExternalStore(
    subscribeToPathname,
    () => window.location.pathname,
    () => '/'
  );
}
